import { createSlice } from '@reduxjs/toolkit'
import {
  equalize,
  loadCountryData,
  loadLegendData,
  loadLegendYearData,
  loadMetricData,
  loadMetricDataTypeMap,
  resetAll,
  checkMetrices,
  setBarChartGlobalData,
  setBarChartNationalData,
  setCurrentYear,
  setGrouping,
  setMetricValue,
  setMultiplierMetric,
  setMultiplierMetricGroup,
  setSortOrder,
  setViewType,
  setShowDwnLoader,
  setShowChartLoader,
  setSubIndex,
  setg20Countries
} from './actions'
import { BarChartGlobalData, BarChartNationalData, Country, MetricGroup, MetricValue, SortOrder, ViewType } from 'types'
import { RootState } from '..'
import { calculateRangePublishYear, shouldDisablePillar } from 'utils'
import { GROUPING_LIST, METRIC_DEFAULT_MULTIPLIER, PILLAR_INFO } from 'constant'

export type SubIndexCodes = 'II' | 'ECI' | 'PGPEI'

export interface VibrancyState {
  viewType: ViewType
  configuration: {
    yearFilter: {
      yearRange: number[]
      currentYear: number
    }
    metricValue: MetricValue
    grouping: {
      groupingList: string[]
      currentGrouping: string
      groupingElement: string[]
    }
  }
  fullCountryData: Country[]
  countryData: Country[]
  metricGroup: MetricGroup[]
  metricDataTypeMap: Record<string, string>
  barChartGlobalData: BarChartGlobalData
  barChartNationalData: BarChartNationalData
  renderChartTimes: number
  renderNationalBarChartTimes: number
  legendData?: Record<string, any>
  showDwnLoader: boolean
  showChartLoader: boolean
  legendYearData?: Record<string, any>
  disabledPillarList: MetricGroup[]
  sortBy: SortOrder
  selectedSubIndex: SubIndexCodes
  g20Countries: boolean
}

const initialState: VibrancyState = {
  viewType: 'bar',
  configuration: {
    yearFilter: {
      yearRange: [],
      currentYear: 0,
    },
    metricValue: 'absolute',
    grouping: {
      groupingList: [],
      currentGrouping: '',
      groupingElement: [],
    },
  },
  fullCountryData: [],
  countryData: [],
  metricGroup: [],
  barChartGlobalData: [],
  barChartNationalData: [],
  metricDataTypeMap: {},
  renderChartTimes: 0,
  renderNationalBarChartTimes: 0,

  legendData: undefined,
  sortBy: SortOrder.groupByPillar,
  disabledPillarList: [],
  showDwnLoader: false,
  showChartLoader: false,
  selectedSubIndex: 'II',
  g20Countries: false
}

export const vibrancySlice = createSlice({
  name: 'AIVibrancyRankings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadCountryData.fulfilled, (state, { payload }) => {
        state.countryData = payload.countryData
        state.fullCountryData = payload.fullCountryData
        const yearRange = calculateRangePublishYear(state.countryData)
        // const groupingList = getAllGrouping(state.countryData)

        state.configuration = {
          yearFilter: {
            currentYear: yearRange[yearRange.length - 1],
            yearRange,
          },
          metricValue: 'absolute',
          grouping: {
            groupingList: GROUPING_LIST.map((el) => el.displayName),
            currentGrouping:
              GROUPING_LIST.find((el) => el.name === 'pillar')?.displayName || GROUPING_LIST[0].displayName,
            groupingElement: [],
          },
        }
      })
      .addCase(loadMetricData.fulfilled, (state, { payload }) => {
        state.metricGroup = payload
      })
      .addCase(loadLegendData.fulfilled, (state, { payload }) => {
        state.legendData = payload
      })
      .addCase(loadLegendYearData.fulfilled, (state, { payload }) => {
        state.legendYearData = payload
      })
      .addCase(loadMetricDataTypeMap.fulfilled, (state, { payload }) => {
        state.metricDataTypeMap = payload
      })
      .addCase(setViewType, (state, { payload }) => {
        state.viewType = payload
      })
      .addCase(setCurrentYear, (state, { payload }) => {
        state.configuration.yearFilter.currentYear = payload
        state.renderChartTimes += 1
      })
      .addCase(setGrouping, (state, { payload }) => {
        state.configuration.grouping.currentGrouping = payload
        state.renderChartTimes += 1
      })
      .addCase(setSortOrder, (state, { payload }) => {
        state.sortBy = payload
      })
      .addCase(setMetricValue, (state, { payload }) => {
        state.configuration.metricValue = payload
        // state.metricGroup.forEach((metricGroup) => {
        //   metricGroup.multiplier = 0.5
        //   metricGroup.metrics.forEach((metric) => (metric.multiplier = 0.5))
        // })
        state.renderChartTimes += 1
      })
      .addCase(setMultiplierMetricGroup, (state, { payload }) => {
        const { metricGroupName, value } = payload
        const metricGroup = state.metricGroup.find((el) => el.metricGroupName === metricGroupName)
        metricGroup!.multiplier = value
        // metricGroup!.metrics.forEach((metric) => {
        //   metric.multiplier = value
        //   return metric
        // })
        state.renderChartTimes += 1
      })
      .addCase(setMultiplierMetric, (state, { payload }) => {
        const { metricGroupName, metricName, value } = payload
        let refinedName = ''
        if (metricName.startsWith('pc_')) {
          refinedName = metricName.replace('pc_', '').trim()
        } else {
          refinedName = 'pc_'.concat(metricName).trim()
        }
        state.metricGroup
          .find((el) => el.metricGroupName === metricGroupName)!
          .metrics.forEach((metric) => {
            if (metric.shortname_raw === metricName || metric.shortname_raw === refinedName) {
              metric.multiplier = value
            }
          })
        state.renderChartTimes += 1
      })
      .addCase(setBarChartNationalData, (state, { payload }) => {
        state.barChartNationalData = payload
      })
      .addCase(setBarChartGlobalData, (state, { payload }) => {
        state.barChartGlobalData = payload
      })
      .addCase(equalize, (state) => {
        state.metricGroup.forEach((metricGroup) => {
          let disabledPillar = false
          if (metricGroup) {
            disabledPillar = shouldDisablePillar(state.countryData, metricGroup, state.configuration.yearFilter.currentYear, state.configuration.metricValue)
          }
          metricGroup.multiplier = !disabledPillar ? 0.5 : 0
          metricGroup.metrics.forEach((metric) => (metric.multiplier = 0.5))
        })
        state.renderChartTimes += 1
      })
      .addCase(resetAll, (state) => {
        state.metricGroup.forEach((metricGroup) => {
          let disabledPillar = false
          if (metricGroup) {
            disabledPillar = shouldDisablePillar(state.countryData, metricGroup, state.configuration.yearFilter.currentYear, state.configuration.metricValue)
          }
          const findPillar = PILLAR_INFO.find((pillar) => pillar.name === metricGroup?.metricGroupName)
          metricGroup.multiplier = !disabledPillar ? findPillar!.defaultMultiplier : 0
          metricGroup.metrics.forEach((metric) => {
            const refinedName = metric?.metricName?.replace(' PC', '')?.trim()
            const mtp = METRIC_DEFAULT_MULTIPLIER?.[refinedName as keyof typeof METRIC_DEFAULT_MULTIPLIER]
            metric.multiplier = mtp ? mtp / 10 : 0
          })
        })
        state.renderChartTimes += 1
      })
      .addCase(checkMetrices, (state) => {                       //For checking if any pillar is disabled or not and persisting user selected values.
        state.metricGroup.forEach((metricGroup) => {
          let disabledPillar = false
          if (metricGroup) {
            disabledPillar = shouldDisablePillar(state.countryData, metricGroup, state.configuration.yearFilter.currentYear, state.configuration.metricValue)
          }
          if (disabledPillar) {
            if (!state.disabledPillarList.find(i => i.metricGroupNameDatakey === metricGroup.metricGroupNameDatakey)) {
              state.disabledPillarList = [...state.disabledPillarList, { ...metricGroup, metrics: [...metricGroup.metrics.map(i => ({ ...i }))] }]
            }
            metricGroup.multiplier = 0
            metricGroup.metrics.forEach((metric) => {
              metric.multiplier = 0
            })
          } else if (state.disabledPillarList.find(i => i.metricGroupNameDatakey === metricGroup.metricGroupNameDatakey)) {
            const findPillar = state.disabledPillarList.find(i => i.metricGroupNameDatakey === metricGroup.metricGroupNameDatakey)
            metricGroup.multiplier = findPillar!.multiplier
            metricGroup.metrics.forEach((metric) => {
              metric.multiplier = findPillar?.metrics.find(i => i.metricName === metric.metricName)?.multiplier || 0
            })
            state.disabledPillarList = state.disabledPillarList.filter(i => i.metricGroupNameDatakey !== metricGroup.metricGroupNameDatakey)
          } else {
            metricGroup.multiplier = metricGroup.multiplier
            metricGroup.metrics.forEach((metric) => {
              metric.multiplier = metric.multiplier
            })
          }
        })
        state.renderChartTimes += 1
      })
      .addCase(setShowDwnLoader, (state, { payload }) => {
        state.showDwnLoader = payload
      })
      .addCase(setShowChartLoader, (state, { payload }) => {
        state.showChartLoader = payload
      })
      .addCase(setSubIndex, (state, { payload }) => {
        state.selectedSubIndex = payload
      })
      .addCase(setg20Countries, (state, { payload }) => {
        state.g20Countries = payload
      })
  },
})

export default vibrancySlice.reducer

export const viewType = (state: RootState) => state.vibrancy.viewType
export const metricValueAIVR = (state: RootState) => state.vibrancy.configuration.metricValue
export const barChartGlobalData = (state: RootState) => state.vibrancy.barChartGlobalData
